import { useEffect, useState } from "react";
import { client } from "@/lib/api";
import { toast } from "sonner";
import { Check, MessageSquareText, ThumbsDown, ThumbsUp, Meh, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const STORAGE_KEY = (slug) => `prid-feedback-${slug}`;

const SHARE_OPTIONS = [
  { key: "yes",   label: "Yes",   Icon: ThumbsUp,   cls: "text-emerald-700 border-emerald-200 bg-emerald-50" },
  { key: "maybe", label: "Maybe", Icon: Meh,        cls: "text-amber-700 border-amber-200 bg-amber-50" },
  { key: "no",    label: "No",    Icon: ThumbsDown, cls: "text-red-700 border-red-200 bg-red-50" },
];

export default function FeedbackCard({ slug }) {
  const [existing, setExisting] = useState(null);   // most-recent server-side submission
  const [dismissed, setDismissed] = useState(false); // localStorage flag
  const [trust, setTrust] = useState(null);
  const [clarity, setClarity] = useState(null);
  const [share, setShare] = useState(null);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  useEffect(() => {
    // Local flag first — avoid re-prompting same visitor
    if (typeof window !== "undefined" && window.localStorage.getItem(STORAGE_KEY(slug))) {
      setDismissed(true);
    }
    // Fetch server state so the report shows "Feedback logged" even for another visitor
    client.get(`/reports/${slug}/feedback`).then((r) => {
      if (r.data?.feedback) setExisting(r.data.feedback);
    }).catch(() => {});
  }, [slug]);

  const canSubmit = trust !== null && clarity !== null && share !== null;

  async function submit() {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      const { data } = await client.post(`/reports/${slug}/feedback`, {
        benchmark_trust: trust,
        report_clarity: clarity,
        share_willingness: share,
        comment: comment || null,
      });
      setSubmitted(data);
      if (typeof window !== "undefined") {
        // Store a bare "1" flag only — no timestamp, no email, no PII
        window.localStorage.setItem(STORAGE_KEY(slug), "1");
      }
      toast.success("Thanks — feedback logged");
    } catch (e) {
      toast.error("Could not save feedback", { description: e?.message });
    } finally {
      setSubmitting(false);
    }
  }

  const already = submitted || existing;

  if (already && (dismissed || submitted)) {
    return (
      <section
        data-testid="feedback-thanks"
        className="border border-emerald-200 bg-emerald-50 p-6 flex items-start gap-3 no-print"
      >
        <Check className="h-5 w-5 text-emerald-700 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-emerald-800 leading-relaxed">
          <div className="font-semibold mb-1">Feedback logged</div>
          Thanks — we've recorded {already.benchmark_trust}/5 for benchmark trust,{" "}
          {already.report_clarity}/5 for clarity, and "{already.share_willingness}" for share intent.
        </div>
      </section>
    );
  }

  return (
    <section
      data-testid="feedback-card"
      className="border border-slate-200 bg-white p-8 no-print"
    >
      <div className="flex items-center gap-3 mb-2">
        <MessageSquareText className="h-4 w-4 text-slate-400" strokeWidth={1.6} />
        <p className="text-xs tracking-[0.28em] uppercase text-slate-500 font-semibold">
          One-click feedback
        </p>
      </div>
      <h3 className="font-heading text-2xl font-medium text-slate-900 mb-6">
        Three quick taps · thirty seconds.
      </h3>

      <div className="space-y-6">
        <RatingRow
          label="How much do you trust the benchmarks?"
          value={trust}
          onChange={setTrust}
          testid="rating-trust"
          lowLabel="Not at all"
          highLabel="Fully"
        />
        <RatingRow
          label="How clear is this report?"
          value={clarity}
          onChange={setClarity}
          testid="rating-clarity"
          lowLabel="Confusing"
          highLabel="Boardroom-ready"
        />

        <div>
          <label className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold block mb-3">
            Would you share this with your CFO or CEO?
          </label>
          <div className="flex flex-wrap gap-2" data-testid="rating-share">
            {SHARE_OPTIONS.map((o) => {
              const active = share === o.key;
              return (
                <button
                  key={o.key}
                  data-testid={`rating-share-${o.key}`}
                  onClick={() => setShare(o.key)}
                  className={`inline-flex items-center gap-2 px-4 py-2 border text-sm transition-all rounded-sm ${
                    active
                      ? `${o.cls} font-semibold shadow-sm scale-[1.02]`
                      : "border-slate-200 bg-white text-slate-600 hover:border-slate-400"
                  }`}
                >
                  <o.Icon className="h-4 w-4" />
                  {o.label}
                </button>
              );
            })}
          </div>
        </div>

        {canSubmit && (
          <div className="pt-2">
            <label className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold block mb-2">
              Anything to add? (optional)
            </label>
            <Textarea
              data-testid="feedback-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="What's the one number you'd challenge?"
              className="rounded-sm border-slate-200 min-h-[80px]"
            />
          </div>
        )}

        <div className="flex items-center gap-4 pt-2">
          <Button
            data-testid="feedback-submit"
            onClick={submit}
            disabled={!canSubmit || submitting}
            className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm shadow-[4px_4px_0_0_#FFC53D] hover:shadow-[2px_2px_0_0_#FFC53D] hover:translate-x-[2px] hover:translate-y-[2px] transition-all"
          >
            <Send className="h-4 w-4 mr-2" />
            {submitting ? "Saving…" : "Send feedback"}
          </Button>
          {!canSubmit && (
            <span className="text-xs text-slate-400">Answer the three above first</span>
          )}
        </div>
      </div>
    </section>
  );
}

function RatingRow({ label, value, onChange, testid, lowLabel, highLabel }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold block mb-3">
        {label}
      </label>
      <div className="flex items-center gap-2" data-testid={testid}>
        <span className="text-[10px] text-slate-400 mono w-16 flex-shrink-0">{lowLabel}</span>
        {[1, 2, 3, 4, 5].map((n) => {
          const active = value === n;
          return (
            <button
              key={n}
              data-testid={`${testid}-${n}`}
              onClick={() => onChange(n)}
              className={`h-11 w-11 border font-mono text-sm transition-all rounded-sm ${
                active
                  ? "bg-slate-900 text-white border-slate-900 font-semibold scale-105 shadow-md"
                  : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"
              }`}
            >
              {n}
            </button>
          );
        })}
        <span className="text-[10px] text-slate-400 mono w-24 flex-shrink-0 text-right">{highLabel}</span>
      </div>
    </div>
  );
}
